/**
 * RGET-3600 / ET-3025 rollout helper. The new Manage Returns design applies only when both
 * `manage_returns_redesign_available` (store BETA_MANAGE_RETURNS) and the per-user UI opt-in
 * are true for the current shop. A stored opt-in alone must not keep the new page or toggle
 * on shops that do not have the feature.
 */
import { APP_PATHS } from "../../../constants/app-paths";
import dashboardSettingsStore from "../../../stores/dashboard-settings-store";
import uiSettingsStore from "../../../stores/ui-settings-store";

export function isManageReturnsRedesignAvailable(): boolean {
    return dashboardSettingsStore.manageReturnsRedesignAvailable;
}

/** True only when the current shop allows the redesign and the user opted in. */
export function shouldUseNewManageReturnsDesign(): boolean {
    return isManageReturnsRedesignAvailable() && uiSettingsStore.manageReturnsNewEnabled;
}

/** Toggle is shop-gated: without the feature there is nothing to switch to. */
export function isManageReturnsDesignToggleVisible(): boolean {
    return isManageReturnsRedesignAvailable();
}

/**
 * Both designs live under the same Laravel page (/dashboard/returns), so switching is a
 * client-side route change that keeps the current query string (filters).
 * Optional `rmaId` keeps the open RMA when flipping designs from a detail route.
 */
export function buildManageReturnsDesignPath(useNewDesign: boolean, rmaId?: string | null): string {
    const basePath = useNewDesign ? APP_PATHS.RETURNS_NEW : APP_PATHS.RETURNS;
    const path = rmaId ? `${basePath}/${rmaId}` : basePath;
    const searchParams = new URLSearchParams(window.location.search);
    // Path carries the RMA id; drop stale query rmaid to avoid Laravel double-targeting on reload.
    searchParams.delete("rmaid");
    const search = searchParams.toString();

    return `${path}${search ? `?${search}` : ""}`;
}

/**
 * Soft SPA links still point at the old `/manage-returns/...` tree. After ui-settings and
 * dashboard settings load, send the user to the design that matches shop feature + preference
 * (Laravel does this only on full reload).
 */
export function getPreferredManageReturnsListPath(): string | null {
    if (!uiSettingsStore.isLoaded || !dashboardSettingsStore.areSettingsLoaded) {
        return null;
    }

    return buildManageReturnsDesignPath(shouldUseNewManageReturnsDesign());
}
